import InputField from 'components/fields/InputField';
import { isValid, parse } from 'date-fns';
import { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';

const AcademicRecords = ({ studentData, handleInputChange, setDisable }) => {
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const INDIAN_STATES = [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry',
  ];

  const COUNTRIES = [
    'India',
    'United States',
    'United Kingdom',
    'Canada',
    'Australia',
    'Germany',
    'France',
    'Japan',
    'China',
    'Brazil',
    'Russia',
    'Italy',
    'South Korea',
    'Netherlands',
    'Spain',
  ];

  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  // const isValidAge = (dob) => {
  //   console.log(dob, 'db');
  //   if (!dob) return false;
  //   const birthDate = new Date(dob);
  //   const today = new Date();
  //   let age = today.getFullYear() - birthDate.getFullYear();
  //   const monthDiff = today.getMonth() - birthDate.getMonth();

  //   if (
  //     monthDiff < 0 ||
  //     (monthDiff === 0 && today.getDate() < birthDate.getDate())
  //   ) {
  //     age--;
  //   }

  //   return age >= 15 && age <= 60;
  // };
  const isValidAge = (dob) => {
    if (!dob) return false;

    try {
      // Parse the date string assuming DD/MM/YYYY format
      const parsedDate = parse(dob, 'dd/MM/yyyy', new Date());

      // Check if the parsed date is valid
      if (!isValid(parsedDate)) {
        return false;
      }

      const today = new Date();
      let age = today.getFullYear() - parsedDate.getFullYear();
      const monthDiff = today.getMonth() - parsedDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < parsedDate.getDate())
      ) {
        age--;
      }

      return age >= 15 && age <= 60;
    } catch (error) {
      console.error('Error parsing date:', error);
      return false;
    }
  };

  const isValidPostalCode = (code) => {
    const postalRegex = /^\d{6}$/;
    return postalRegex.test(code);
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        setFieldErrors((prev) => ({
          ...prev,
          profilePicture: 'Only JPEG, PNG, and GIF images are allowed',
        }));
        e.target.value = '';
        setProfilePicPreview(null);
        return;
      }

      if (file.size > maxSize) {
        setFieldErrors((prev) => ({
          ...prev,
          profilePicture: 'File size should not exceed 5MB',
        }));
        e.target.value = '';
        setProfilePicPreview(null);
        return;
      }

      const { profilePicture, ...restErrors } = fieldErrors;
      setFieldErrors(restErrors);

      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicPreview(reader.result);
        handleInputChange({
          target: {
            id: 'profilePicture',
            files: [file],
          },
        });
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const newFieldErrors = {};

    if (studentData.email.trim() && !isValidEmail(studentData.email)) {
      newFieldErrors.email = 'Please enter a valid email address';
    }

    // phone number
    if (
      studentData.contactNumber.trim() &&
      !isValidPhone(studentData.contactNumber)
    ) {
      newFieldErrors.contactNumber =
        'Please enter a valid 10-digit mobile number starting with 6-9';
    }

    // dobb
    if (
      studentData.dateOfBirth.trim() &&
      !isValidAge(studentData.dateOfBirth)
    ) {
      newFieldErrors.dateOfBirth =
        'Student age must be between 15 and 60 years';
    }

    // postal code
    if (
      studentData.address.postalCode.trim() &&
      !isValidPostalCode(studentData.address.postalCode)
    ) {
      newFieldErrors.postalCode = 'Please enter a valid 6-digit postal code';
    }

    setFieldErrors(newFieldErrors);

    const isAnyFieldEmpty =
      !studentData.name.trim() ||
      !studentData.email.trim() ||
      !studentData.contactNumber.trim() ||
      !studentData.dateOfBirth.trim() ||
      !studentData.gender.trim() ||
      !studentData.address.street.trim() ||
      !studentData.address.city.trim() ||
      !studentData.address.state.trim() ||
      !studentData.address.postalCode.trim() ||
      !studentData.address.country.trim() ||
      !studentData.profilePicture;

    if (isAnyFieldEmpty) {
      setError('All fields are mandatory.');
      setDisable(true);
    } else if (Object.keys(newFieldErrors).length > 0) {
      setError('Please correct the validation errors below.');
      setDisable(true);
    } else {
      setError('');
      setDisable(false);
    }
  }, [studentData, setDisable]);

  return (
    <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">
      <h4 className="pt-[5px] text-xl font-bold text-navy-700 dark:text-white">
        Create Student Profile :
      </h4>

      <div className="mb-5 flex flex-col items-center">
        <label className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
          Profile Picture*
        </label>
        <div className="flex w-full items-center justify-center">
          <div className="relative">
            {profilePicPreview ? (
              <img
                src={profilePicPreview}
                alt="Profile Preview"
                className="h-40 w-40 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-40 w-40 items-center justify-center rounded-full bg-gray-200">
                <span className="text-gray-500">No image</span>
              </div>
            )}
            <input
              type="file"
              id="profilePicture"
              accept="image/jpeg,image/png,image/gif"
              onChange={handleProfilePicChange}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
            <label
              htmlFor="profilePicture"
              className="absolute bottom-0 right-0 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-blue-500 text-white"
            >
              ✏️
            </label>
          </div>
        </div>
        {fieldErrors.profilePicture && (
          <p className="mt-2 text-sm text-red-500">
            {fieldErrors.profilePicture}
          </p>
        )}
      </div>

      {/* Basic Information */}
      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <InputField
            extra="mb-3"
            label="Name"
            placeholder="Enter Student Name"
            id="name"
            onChange={handleInputChange}
            value={studentData.name}
            maxLength={40}
          />
        </div>
        <div>
          <InputField
            extra="mb-3"
            label="Email"
            placeholder="Enter Student Email"
            id="email"
            type="email"
            onChange={handleInputChange}
            value={studentData.email}
            maxLength={40}
          />
          {fieldErrors.email && (
            <p className="mt-1 text-sm text-red-500">{fieldErrors.email}</p>
          )}
        </div>
        <div>
          <InputField
            extra="mb-3"
            label="Contact Number"
            placeholder="Enter Contact Number"
            id="contactNumber"
            type="tel"
            onChange={handleInputChange}
            value={studentData.contactNumber}
            maxLength={10}
          />
          {fieldErrors.contactNumber && (
            <p className="mt-1 text-sm text-red-500">
              {fieldErrors.contactNumber}
            </p>
          )}
        </div>
        <div className="w-full">
          <label className="ml-2 mt-1 text-sm font-bold text-navy-700 dark:text-white">
            Date of Birth <span className="ml-1 text-red-500">*</span>
          </label>
          <input
            type="date"
            id="dateOfBirth"
            className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:border-white/10 dark:text-white"
            value={studentData.dateOfBirth ? studentData.dateOfBirth.split('/').reverse().join('-') : ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val) {
                const [y, m, d] = val.split('-');
                handleInputChange({ target: { id: 'dateOfBirth', value: `${d}/${m}/${y}` } });
              }
            }}
          />
          {fieldErrors.dateOfBirth && (
            <p className="mt-1 text-sm text-red-500">
              {fieldErrors.dateOfBirth}
            </p>
          )}
        </div>
        <div>
          <label className="mb-3 ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Gender
          </label>
          <select
            id="gender"
            onChange={handleInputChange}
            value={studentData.gender}
            className="mb-1 mt-1.5 w-full rounded-xl border border-gray-200 py-3.5 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
        </div>
      </div>

      {/* Address Information */}
      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <InputField
          extra="mb-3"
          label="Address"
          placeholder="Enter your address details"
          id="address.street"
          onChange={handleInputChange}
          value={studentData.address.street}
          maxLength={100}
        />
        <InputField
          extra="mb-3"
          label="City"
          placeholder="Enter City"
          id="address.city"
          onChange={handleInputChange}
          value={studentData.address.city}
          maxLength={20}
        />
        <div>
          <label className="mb-3 ml-3 text-sm font-bold text-navy-700 dark:text-white">
            State
          </label>
          <select
            id="address.state"
            onChange={handleInputChange}
            value={studentData.address.state}
            className="mb-1 mt-1.5 w-full rounded-xl border border-gray-200 py-3.5 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Select State</option>
            {INDIAN_STATES.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </div>
        <div>
          <InputField
            extra="mb-3"
            label="Postal Code"
            placeholder="Enter Postal Code"
            id="address.postalCode"
            onChange={handleInputChange}
            value={studentData.address.postalCode}
            maxLength={6}
          />
          {fieldErrors.postalCode && (
            <p className="mt-1 text-sm text-red-500">
              {fieldErrors.postalCode}
            </p>
          )}
        </div>
        <div>
          <label className="mb-3 ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Country
          </label>
          <select
            id="address.country"
            onChange={handleInputChange}
            value={studentData.address.country}
            className="mb-1 mt-1.5 w-full rounded-xl border border-gray-200 py-3.5 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Select Country</option>
            {COUNTRIES.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Display main error message */}
      {error && (
        <div className="mt-3 text-red-500">
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default AcademicRecords;
